/**
 * Community hooks: posts list, like, create post, add comment.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchPosts, likePost, createPost, addComment, type CreatePostPayload } from './api';
import { useCommunityFeatureEnabled } from '../config';
import type { CommunityPost } from './apiTypes';
import { communityQueryKeys } from './keys';

const EMPTY_POSTS: CommunityPost[] = [];

export function usePosts() {
  const community_enabled = useCommunityFeatureEnabled();
  const query = useQuery({
    queryKey: communityQueryKeys.posts,
    queryFn: fetchPosts,
    enabled: community_enabled,
  });
  const list: CommunityPost[] = Array.isArray(query.data) && query.data.length > 0 ? query.data : EMPTY_POSTS;
  return {
    data: list,
    postsData: list,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}

export function useLikePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (params: { postId: number; customerId: number }) =>
      likePost({ post_id: params.postId, customer_id: params.customerId }),
    onMutate: async params => {
      await client.cancelQueries({ queryKey: communityQueryKeys.posts });
      const previous = client.getQueryData<CommunityPost[]>(communityQueryKeys.posts);
      client.setQueryData<CommunityPost[]>(communityQueryKeys.posts, old => {
        const list = Array.isArray(old) ? old : [];
        return list.map(post => {
          if (post.id !== params.postId) {
            return post;
          }
          const was_reacted = Boolean(post.is_reacted);
          const next_reacted = !was_reacted;
          const base = Number(post.total_reactions) || 0;
          return {
            ...post,
            is_reacted: next_reacted,
            total_reactions: Math.max(0, base + (next_reacted ? 1 : -1)),
          };
        });
      });
      return { previous };
    },
    onError: (_err, _params, ctx) => {
      if (ctx?.previous) {
        client.setQueryData(communityQueryKeys.posts, ctx.previous);
      }
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: communityQueryKeys.posts });
    },
  });
}

export function useCreatePost() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePostPayload) => createPost(data),
    onSuccess: () => client.invalidateQueries({ queryKey: communityQueryKeys.posts }),
  });
}

export function useAddComment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (data: { post_id: number; customer_id: number; content: string }) => addComment(data),
    onSuccess: () => client.invalidateQueries({ queryKey: communityQueryKeys.posts }),
  });
}
